import type { CallSessionId, PublicKey } from '@shared/types';
import { wsClient } from '../services/websocket';
import { stopNativeIncomingRinging } from '../services/callRuntime';
import { t } from '../i18n';
import { resolveCallActionContext } from '../services/actionContextResolver';
import { useCallStore } from '../store/callStore';
import { callTabCoordination } from '../services/callTabCoordination';
import { cleanupAfterFailedAnswer } from './callCleanup';
import { getCallRuntimeAdapter } from './callRuntimeAdapters';
import type { CallEndInfo, OutgoingIdentityTarget } from './callTypes';
import { createIncomingActiveCall, createOutgoingActiveCall } from './callStateTransitions';
import type { CallStateAction } from './callStateReducer';
import { createCallEffects } from './callEffects';
import { createCallEndHandler } from './callLifecycle';
import { humanizeCallError, isMediaPermissionError } from './callErrorMessages';
import {
  forgetCancelledOutgoingCallSession,
  rememberCancelledOutgoingCallSession,
  rememberIgnoredIncomingCallSession,
  shouldCancelOutgoingCallSession
} from './callSessionEndGuards';
import type { SigningKey } from '../crypto/ed25519Provider';
import { isWebViewMode } from '../utils/webviewBridge';
import { rememberCallReturnPath } from './callNavigationState';
import { hasCircleServerAccessRemoved } from '../utils/removedCircleAccess';

type ContactIdentity = {
  identityId: string;
  identityPublicKey?: PublicKey;
  serverId?: string;
  serverUrl?: string;
};

type ContactLike = {
  contactId: string;
  displayName: string;
  identities: ContactIdentity[];
};

type IdentityCallParams = OutgoingIdentityTarget & {
  localIdentityId?: string;
  withVideo?: boolean;
  identitySigningKey?: SigningKey;
  isKnownContact?: boolean;
};

type CreateCallOrchestratorParams = {
  contacts: ContactLike[];
  navigate: (to: string) => void;
  endCallState: ReturnType<typeof useCallStore.getState>['endCall'];
  dispatchCallState: (action: CallStateAction) => void;
  tryUnlockCallSounds: () => Promise<boolean>;
  ensureNoOngoingCall: () => boolean;
  clearPendingCallAction: () => void;
  stopRingtone: () => void;
  stopOutgoingDialTone: () => void;
  playOutgoingDialTone: () => void;
  playRingtone: () => void;
  isLocalIncomingCallLeader: (callSessionId: string) => boolean;
  markCallEndedInHistory: (params: {
    callSessionId: string;
    activeCall?: import('../store/callStore').ActiveCall | null;
    incomingCall?: import('../store/callStore').IncomingCall | null;
    reason?: string;
    source?: CallEndInfo['source'];
    durationSeconds?: number;
  }) => void;
  getActiveCallDurationSeconds: () => number | undefined;
  preflightWebVideoMediaAccess: () => Promise<void>;
  consumePendingPostCallJoin: (callSessionId: string) => { inviteToken: string; serverUrl: string } | null;
  onLocalCallEnded?: (info?: CallEndInfo) => void;
};

export function createCallOrchestrator(params: CreateCallOrchestratorParams) {
  const captureReturnPath = () => {
    if (typeof window === 'undefined') return;
    rememberCallReturnPath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
  };

  const effects = createCallEffects({
    markCallEndedInHistory: params.markCallEndedInHistory,
    getActiveCallDurationSeconds: params.getActiveCallDurationSeconds,
    consumePendingPostCallJoin: params.consumePendingPostCallJoin,
    navigate: params.navigate
  });

  const handleCallEnd = createCallEndHandler({
    navigate: params.navigate,
    clearPendingCallAction: params.clearPendingCallAction,
    stopRingtone: params.stopRingtone,
    stopOutgoingDialTone: params.stopOutgoingDialTone,
    endCallState: params.endCallState,
    setLastCallEndInfo: useCallStore.getState().setLastCallEndInfo,
    markCallEndedInHistory: params.markCallEndedInHistory,
    getActiveCallDurationSeconds: params.getActiveCallDurationSeconds,
    consumePendingPostCallJoin: params.consumePendingPostCallJoin,
    onLocalCallEnded: params.onLocalCallEnded
  });

  const startOutgoingCall = async (target: IdentityCallParams) => {
    const remoteIdentityId = (target.remoteIdentityId || '').trim();
    if (!remoteIdentityId) {
      throw new Error(t('call.errors.noIdentityAvailable'));
    }

    const withVideo = target.withVideo === true;
    let signingKey = target.identitySigningKey;
    const runtimeAdapter = getCallRuntimeAdapter();
    const callSessionId: CallSessionId = `call_temp_${Date.now()}`;
    let lockAcquired = false;

    const outgoingCall = createOutgoingActiveCall({
      callSessionId,
      remoteIdentityId,
      localIdentityId: target.localIdentityId,
      serverId: (target.serverId || '').toString(),
      serverName: (target.serverId || '').toString(),
      serverUrl: target.serverUrl,
      isKnownContact: target.isKnownContact === true,
      remoteDisplayName: (target.remoteDisplayName || remoteIdentityId).toString(),
      remoteIdentityName: target.serverIdentityName,
      remoteIdentityPublicKey: target.remoteIdentityPublicKey,
      videoEnabled: withVideo,
      audioEnabled: true
    });
    if (isWebViewMode) {
      outgoingCall.nativeRuntimeMode = 'video-native';
    }

    params.dispatchCallState({
      type: 'call/outgoing-started',
      call: outgoingCall
    });

    captureReturnPath();

    if (!isWebViewMode) {
      params.navigate('/call');
    }

    try {
      const callContext = await resolveCallActionContext({
        targetIdentityId: remoteIdentityId,
        targetServerId: target.serverId,
        targetServerUrl: target.serverUrl,
        localIdentityId: target.localIdentityId,
        timeoutMs: 10_000
      });
      const resolvedLocalIdentityId = callContext.localIdentityId;
      const resolvedLocalDeviceId = callContext.localDeviceId;
      const resolvedLocalServerUrl = callContext.localServerUrl;
      const deviceSigningKey = callContext.deviceSigningKey;
      if (hasCircleServerAccessRemoved(resolvedLocalIdentityId)) {
        throw new Error(t('servers.serverAccessRemovedAction'));
      }
      if (!signingKey) {
        signingKey = callContext.signingKey;
      }

      params.dispatchCallState({
        type: 'call/active-updated',
        patch: {
          localIdentityId: resolvedLocalIdentityId,
          connectionPhase: 'preparing'
        }
      });

      await runtimeAdapter.prepareLocalMedia({ withVideo });

      wsClient.acquireCallLock({
        callSessionId,
        deviceId: resolvedLocalDeviceId || '',
        identityId: resolvedLocalIdentityId,
        serverUrl: resolvedLocalServerUrl || target.serverUrl || ''
      });
      lockAcquired = true;

      await runtimeAdapter.startOutgoing({
        callSessionId,
        remoteIdentityId,
        localIdentityId: resolvedLocalIdentityId,
        localDeviceId: resolvedLocalDeviceId,
        remoteDisplayName: (target.remoteDisplayName || remoteIdentityId).toString(),
        remoteIdentityPublicKey: target.remoteIdentityPublicKey,
        withVideo,
        serverUrl: target.serverUrl,
        signingKey,
        deviceSigningKey
      });

      if (shouldCancelOutgoingCallSession(callSessionId)) {
        getCallRuntimeAdapter().end(callSessionId);
        wsClient.releaseCallLock(callSessionId);
        return callSessionId;
      }

      forgetCancelledOutgoingCallSession(callSessionId);
      wsClient.promoteCallLock(callSessionId, 'active-call');
      params.dispatchCallState({ type: 'call/outgoing-ringing' });
      params.playOutgoingDialTone();
      return callSessionId;
    } catch (error) {
      if (lockAcquired) {
        wsClient.releaseCallLock(callSessionId);
      }
      try {
        runtimeAdapter.end(callSessionId);
      } catch {
        // ignore cleanup errors; the preparation error should stay visible.
      }
      params.dispatchCallState({
        type: 'call/active-updated',
        patch: {
          status: 'ending',
          connectionPhase: undefined,
          preparationError: t('call.errors.startFailed', { message: humanizeCallError(error) })
        }
      });
      return callSessionId;
    }
  };

  const initiateCall = async (contactId: string, withVideo = false, identityId?: string) => {
    try {
      if (!params.ensureNoOngoingCall()) return;
      void params.tryUnlockCallSounds();

      const contact = params.contacts.find((item) => item.contactId === contactId);
      if (!contact) {
        throw new Error(t('call.errors.contactNotFound'));
      }

      let selectedIdentity = contact.identities[0];
      if (identityId) {
        const found = contact.identities.find((item) => item.identityId === identityId);
        if (found) {
          selectedIdentity = found;
        } else {
          throw new Error(t('call.errors.identityNotFound'));
        }
      }

      if (!selectedIdentity) {
        throw new Error(t('call.errors.noIdentityAvailable'));
      }

      await startOutgoingCall({
        remoteIdentityId: selectedIdentity.identityId,
        remoteDisplayName: contact.displayName,
        remoteIdentityPublicKey: selectedIdentity.identityPublicKey,
        serverId: selectedIdentity.serverId,
        serverUrl: selectedIdentity.serverUrl,
        withVideo,
        isKnownContact: true
      });
    } catch (error) {
      console.error('Failed to initiate call:', error);
      if (!isMediaPermissionError(error)) {
        effects.showStartFailed(t('call.errors.startFailed', { message: humanizeCallError(error) }));
      }
      handleCallEnd();
    }
  };

  const initiateCallToIdentity = async (target: IdentityCallParams) => {
    try {
      if (!params.ensureNoOngoingCall()) return null;
      void params.tryUnlockCallSounds();
      return await startOutgoingCall(target);
    } catch (error) {
      console.error('Failed to initiate call to identity:', error);
      if (!isMediaPermissionError(error)) {
        effects.showStartFailed(t('call.errors.startFailed', { message: humanizeCallError(error) }));
      }
      handleCallEnd();
      return null;
    }
  };

  const answerCall = async (withVideo = false) => {
    const incomingCall = useCallStore.getState().incomingCall;
    if (!incomingCall) {
      console.error('No incoming call to answer');
      return;
    }

    try {
      stopNativeIncomingRinging(incomingCall.callSessionId);
      callTabCoordination.startManagingIncomingCall(incomingCall.callSessionId);

      const callContext = await resolveCallActionContext({
        targetIdentityId: incomingCall.fromIdentityId,
        targetServerId: incomingCall.localServerId,
        targetServerUrl: incomingCall.localServerUrl,
        timeoutMs: 10_000
      });

      void params.tryUnlockCallSounds();

      const signingKey = callContext.signingKey;
      const deviceSigningKey = callContext.deviceSigningKey;
      if (!signingKey) {
        params.clearPendingCallAction();
        effects.showKeysNotReady(t('call.errors.keysNotReady'));
        return;
      }

      const runtimeAdapter = getCallRuntimeAdapter();
      params.stopRingtone();
      await runtimeAdapter.prepareLocalMedia({ withVideo });
      const incomingAnswerCall = createIncomingActiveCall({
        callSessionId: incomingCall.callSessionId,
        remoteIdentityId: incomingCall.fromIdentityId,
        localIdentityId: callContext.localIdentityId,
        serverId: incomingCall.localServerId,
        serverName: incomingCall.localServerId,
        serverUrl: incomingCall.localServerUrl,
        isKnownContact: incomingCall.isKnownContact,
        isTemporaryLinkCall: incomingCall.isTemporaryLinkCall,
        remoteDisplayName: incomingCall.fromDisplayName,
        remoteIdentityName: incomingCall.fromIdentityName,
        remoteIdentityPublicKey: incomingCall.fromIdentityPublicKey,
        videoEnabled: withVideo,
        audioEnabled: true,
        securityVerified: incomingCall.e2eeVerified === true
      });

      params.dispatchCallState({
        type: 'call/incoming-answer-started',
        call: incomingAnswerCall
      });

      captureReturnPath();

      if (!isWebViewMode) {
        params.navigate('/call');
      }

      wsClient.acquireCallLock({
        callSessionId: incomingCall.callSessionId,
        deviceId: callContext.localDeviceId,
        identityId: callContext.localIdentityId,
        serverUrl: callContext.localServerUrl || incomingCall.localServerUrl || ''
      });

      params.dispatchCallState({
        type: 'call/active-updated',
        patch: {
          connectionPhase: 'negotiating'
        }
      });

      await runtimeAdapter.answerIncoming({
        callSessionId: incomingCall.callSessionId,
        remoteIdentityId: incomingCall.fromIdentityId,
        localIdentityId: callContext.localIdentityId,
        localDeviceId: callContext.localDeviceId,
        remoteDisplayName: incomingCall.fromDisplayName,
        remoteIdentityPublicKey: incomingCall.fromIdentityPublicKey,
        withVideo,
        serverUrl: incomingCall.localServerUrl,
        signingKey,
        deviceSigningKey,
        offer: incomingCall.offer
      });

      params.dispatchCallState({
        type: 'call/active-updated',
        patch: {
          connectionPhase: 'finding_route'
        }
      });

      wsClient.promoteCallLock(incomingCall.callSessionId, 'active-call');
      params.stopRingtone();
      params.stopOutgoingDialTone();
      callTabCoordination.noteCallAction(incomingCall.callSessionId, 'accepted');
      callTabCoordination.stopManagingIncomingCall(incomingCall.callSessionId);
    } catch (error) {
      console.error('Failed to answer call:', error);
      wsClient.releaseCallLock(incomingCall.callSessionId);
      effects.showAnswerFailed(t('call.errors.answerFailed', { message: humanizeCallError(error) }));

      cleanupAfterFailedAnswer({
        incomingCall,
        dispatchCallState: params.dispatchCallState,
        playRingtone: params.playRingtone,
        stopRingtone: params.stopRingtone,
        isLocalIncomingCallLeader: params.isLocalIncomingCallLeader,
        onFallbackFailure: () => handleCallEnd({ reason: 'answer_failed', source: 'unknown' })
      });
    }
  };

  const rejectCall = async () => {
    const incomingCall = useCallStore.getState().incomingCall;
    const activeCall = useCallStore.getState().activeCall;
    if (!incomingCall) return;
    rememberIgnoredIncomingCallSession(incomingCall.callSessionId);
    stopNativeIncomingRinging(incomingCall.callSessionId);

    if (activeCall && activeCall.callSessionId === incomingCall.callSessionId) {
      params.dispatchCallState({
        type: 'set-incoming-call',
        call: null
      });
      params.stopRingtone();
      return;
    }

    void params.tryUnlockCallSounds();
    params.markCallEndedInHistory({
      callSessionId: incomingCall.callSessionId,
      incomingCall,
      reason: 'tab_rejected',
      source: 'user'
    });
    try {
      const callContext = await resolveCallActionContext({
        targetIdentityId: incomingCall.fromIdentityId,
        targetServerId: incomingCall.localServerId,
        targetServerUrl: incomingCall.localServerUrl,
        timeoutMs: 10_000
      });
      wsClient.acquireCallLock({
        callSessionId: incomingCall.callSessionId,
        deviceId: callContext.localDeviceId,
        identityId: callContext.localIdentityId,
        serverUrl: callContext.localServerUrl || incomingCall.localServerUrl || ''
      });
      wsClient.endCall(incomingCall.callSessionId);
      wsClient.releaseCallLock(incomingCall.callSessionId);
    } catch (error) {
      console.error('Failed to align context for reject, falling back to current WS context:', error);
      wsClient.endCall(incomingCall.callSessionId);
    }
    callTabCoordination.noteCallAction(incomingCall.callSessionId, 'rejected');
    params.clearPendingCallAction();
    params.dispatchCallState({
      type: 'set-incoming-call',
      call: null
    });
    params.stopRingtone();
  };

  const endActiveCall = () => {
    const activeCall = useCallStore.getState().activeCall;
    if (activeCall) {
      if (activeCall.isInitiator && (activeCall.status === 'initiating' || activeCall.status === 'ringing' || activeCall.status === 'connecting')) {
        rememberCancelledOutgoingCallSession(activeCall.callSessionId);
      }
      try {
        wsClient.endCall(activeCall.callSessionId);
        callTabCoordination.noteCallAction(activeCall.callSessionId, 'ended');
      } catch {
        // ignore
      }
      getCallRuntimeAdapter().end(activeCall.callSessionId);
    }
    handleCallEnd({ reason: 'user_hangup', source: 'user' });
  };

  return {
    initiateCall,
    initiateCallToIdentity,
    answerCall,
    rejectCall,
    endActiveCall,
    handleCallEnd
  };
}
